import { t, Instance } from 'mobx-state-tree';
import { DEFAULT_GENDER } from '@/types';
import { familyStore } from './FamilyStore';
import { RootGenerationModel } from './models/RootGeneration';
import { ParentsGenerationModel } from './models/ParentsGeneration';
import { ChildrenGenerationModel } from './models/ChildrenGeneration';
import { GrandparentsGenerationModel } from './models/GrandparentsGeneration';
import { determineDisplaySpouse, determinePositions } from '@/utils/spouses';
import { selectDisplayParents } from '@/utils/parentSelection';

/**
 * HybridPedigreeStore - Manages the tree visualization state
 * Uses MST references to PersonModel instances from FamilyStore
 */
export const HybridPedigreeStore = t
  .model('HybridPedigreeStore', {
    treeAnchorFamilyMemberId: t.string,
    // Loading state
    isBuilding: t.optional(t.boolean, false),
    lastBuilt: t.maybeNull(t.Date),
    // Generation structures - using the imported models
    grandparentsGen: GrandparentsGenerationModel,
    parentsGen: ParentsGenerationModel,
    rootGen: RootGenerationModel,
    childrenGen: ChildrenGenerationModel
  })
  .views((self) => ({
    get isLoaded(): boolean {
      return !self.isBuilding && self.lastBuilt !== null;
    },

    get hasTreeAnchor(): boolean {
      return self.treeAnchorFamilyMemberId !== null;
    },
    /**
     * Check if the TreeAnchor is a placeholder person
     * When TreeAnchor is a placeholder, parent and grandparent PersonCards should not be clickable
     * (since clicking them would try to set a placeholder as the new TreeAnchor, which doesn't make sense)
     */
    get isTreeAnchorPlaceholder(): boolean {
      if (!self.treeAnchorFamilyMemberId) {
        return false;
      }
      const treeAnchor = familyStore.getPerson(self.treeAnchorFamilyMemberId);
      return treeAnchor?.isPlaceholder ?? false;
    },
    /**
     * Return the family member ID of the spouse that is currently displayed next to
     * the given person in the tree (root, parents, or grandparents row), or null if
     * the person is not in a displayed partnership.
     *
     * This is the source of truth for things like the spouse switcher's selected/highlighted
     * spouse — the tree builder may pick a biological spouse over the person's
     * `selectedSpouseFamilyMemberId` (e.g. for the anchor's parents row), and the UI
     * should reflect what is actually rendered, not the underlying PersonModel selection.
     */
    getDisplayedSpouseFamilyMemberId(familyMemberId: string): string | null {
      if (!familyMemberId) return null;
      const { leftRootFamilyMemberId, rightRootFamilyMemberId } = self.rootGen;
      if (familyMemberId === leftRootFamilyMemberId) return rightRootFamilyMemberId ?? null;
      if (familyMemberId === rightRootFamilyMemberId) return leftRootFamilyMemberId ?? null;

      const { leftParentFamilyMemberId, rightParentFamilyMemberId } = self.parentsGen;
      if (familyMemberId === leftParentFamilyMemberId) return rightParentFamilyMemberId ?? null;
      if (familyMemberId === rightParentFamilyMemberId) return leftParentFamilyMemberId ?? null;

      const {
        leftGrandparentsLeftFamilyMemberId,
        leftGrandparentsRightFamilyMemberId,
        rightGrandparentsLeftFamilyMemberId,
        rightGrandparentsRightFamilyMemberId
      } = self.grandparentsGen;
      if (familyMemberId === leftGrandparentsLeftFamilyMemberId) return leftGrandparentsRightFamilyMemberId ?? null;
      if (familyMemberId === leftGrandparentsRightFamilyMemberId) return leftGrandparentsLeftFamilyMemberId ?? null;
      if (familyMemberId === rightGrandparentsLeftFamilyMemberId) return rightGrandparentsRightFamilyMemberId ?? null;
      if (familyMemberId === rightGrandparentsRightFamilyMemberId) return rightGrandparentsLeftFamilyMemberId ?? null;

      return null;
    }
  }))
  .actions((self) => ({
    /**
     * Clear all generation data
     */
    clearTree() {
      self.grandparentsGen.clear();
      self.parentsGen.clear();
      self.rootGen.clear();
      self.childrenGen.clear();
      self.lastBuilt = null;
    },

    /**
     * Clear all tree state and reset to initial state
     * Called when user signs out to ensure fresh state for next user
     */
    clear() {
      this.clearTree();
      self.treeAnchorFamilyMemberId = '';
      self.isBuilding = false;
      self.lastBuilt = null;
    },

    /**
     * Build the entire tree structure starting from tree anchor
     * Uses references to PersonModel instances from FamilyStore
     */
    buildTree(treeAnchorFamilyMemberId: string) {
      self.isBuilding = true;
      self.treeAnchorFamilyMemberId = treeAnchorFamilyMemberId;

      try {
        // Clear existing tree
        this.clearTree();

        if (!familyStore.isLoaded) {
          // eslint-disable-next-line no-console
          console.warn('[HybridPedigreeStore] FamilyStore not loaded, cannot build tree');
          throw new Error('FamilyStore must be loaded before building tree');
        }

        // Check if FamilyStore actually has people loaded
        if (familyStore.people.size === 0) {
          // eslint-disable-next-line no-console
          console.error('[HybridPedigreeStore] FamilyStore is marked as loaded but has no people', {
            familyId: familyStore.familyId,
            isLoaded: familyStore.isLoaded,
            isLoading: familyStore.isLoading
          });
          throw new Error('FamilyStore has no people loaded. Cannot build tree.');
        }

        const treeAnchor = familyStore.getPerson(treeAnchorFamilyMemberId);
        if (!treeAnchor) {
          // eslint-disable-next-line no-console
          console.error('[HybridPedigreeStore] Tree anchor not found', {
            treeAnchorFamilyMemberId,
            familyStoreIsLoaded: familyStore.isLoaded,
            peopleCount: familyStore.people.size,
            availableFamilyMemberIds: Array.from(familyStore.people.keys())
          });
          throw new Error(`Tree anchor ${treeAnchorFamilyMemberId} not found in FamilyStore`);
        }

        // STEP 1: Set root generation
        // The tree anchor is the person the tree is based on
        // Determine the spouse to display and their positions
        const displaySpouseFamilyMemberId = determineDisplaySpouse(treeAnchor);
        const displaySpouse = displaySpouseFamilyMemberId ? familyStore.getPerson(displaySpouseFamilyMemberId) : null;
        const rootPositions = determinePositions(treeAnchor, displaySpouse ?? null);
        self.rootGen.setLeftRootFamilyMemberId(rootPositions.leftFamilyMemberId ?? null);
        self.rootGen.setRightRootFamilyMemberId(rootPositions.rightFamilyMemberId ?? null);

        // Create placeholders for unknown root generation positions only when the root has children.
        // When the root is the last generation (no child generation), showing an unknown spouse is confusing.
        const rootChildren = familyStore.getChildren(treeAnchorFamilyMemberId);
        const rootHasChildren = rootChildren.length > 0;
        if (rootHasChildren) {
          // Use tree anchor ID to make placeholder IDs unique per tree build
          if (!self.rootGen.leftRootFamilyMemberId) {
            const placeholderId = `placeholder-${treeAnchorFamilyMemberId}-left-root`;
            this.ensurePlaceholderPerson(placeholderId, 'left-root');
            self.rootGen.setLeftRootFamilyMemberId(placeholderId);
          }
          if (!self.rootGen.rightRootFamilyMemberId) {
            const placeholderId = `placeholder-${treeAnchorFamilyMemberId}-right-root`;
            this.ensurePlaceholderPerson(placeholderId, 'right-root');
            self.rootGen.setRightRootFamilyMemberId(placeholderId);
          }
        }

        // STEP 2: Set parents generation
        // Use the parent selection strategy to determine which 2 parents to display.
        // For the anchor, prefer biological (etc.) over step via relationship type — not spouse UI selection.
        const parentSelection = selectDisplayParents(treeAnchorFamilyMemberId, familyStore, {
          defaultParentsByRelationshipType: true
        });
        self.parentsGen.setLeftParentFamilyMemberId(parentSelection.leftParent?.familyMemberId ?? null);
        self.parentsGen.setRightParentFamilyMemberId(parentSelection.rightParent?.familyMemberId ?? null);

        // Create placeholders for unknown parent positions
        // Use tree anchor ID to make placeholder IDs unique per tree build
        if (!self.parentsGen.leftParentFamilyMemberId) {
          const placeholderId = `placeholder-${treeAnchorFamilyMemberId}-left-parent`;
          this.ensurePlaceholderPerson(placeholderId, 'left-parent');
          self.parentsGen.setLeftParentFamilyMemberId(placeholderId);
        }
        if (!self.parentsGen.rightParentFamilyMemberId) {
          const placeholderId = `placeholder-${treeAnchorFamilyMemberId}-right-parent`;
          this.ensurePlaceholderPerson(placeholderId, 'right-parent');
          self.parentsGen.setRightParentFamilyMemberId(placeholderId);
        }

        // STEP 3: Set grandparents generation
        // Use the parent selection strategy for each parent to determine their parents (grandparents)
        const leftParentFamilyMemberId = self.parentsGen.leftParentFamilyMemberId;
        const rightParentFamilyMemberId = self.parentsGen.rightParentFamilyMemberId;

        if (leftParentFamilyMemberId) {
          const leftGrandparentSelection = selectDisplayParents(leftParentFamilyMemberId, familyStore);
          self.grandparentsGen.setLeftGrandparentsLeftFamilyMemberId(
            leftGrandparentSelection.leftParent?.familyMemberId ?? null
          );
          self.grandparentsGen.setLeftGrandparentsRightFamilyMemberId(
            leftGrandparentSelection.rightParent?.familyMemberId ?? null
          );
        }

        // Create placeholders for unknown left grandparent positions
        // Use tree anchor ID to make placeholder IDs unique per tree build
        if (!self.grandparentsGen.leftGrandparentsLeftFamilyMemberId) {
          const placeholderId = `placeholder-${treeAnchorFamilyMemberId}-left-grandparent-left`;
          this.ensurePlaceholderPerson(placeholderId, 'left-grandparent-left');
          self.grandparentsGen.setLeftGrandparentsLeftFamilyMemberId(placeholderId);
        }
        if (!self.grandparentsGen.leftGrandparentsRightFamilyMemberId) {
          const placeholderId = `placeholder-${treeAnchorFamilyMemberId}-left-grandparent-right`;
          this.ensurePlaceholderPerson(placeholderId, 'left-grandparent-right');
          self.grandparentsGen.setLeftGrandparentsRightFamilyMemberId(placeholderId);
        }

        if (rightParentFamilyMemberId) {
          const rightGrandparentSelection = selectDisplayParents(rightParentFamilyMemberId, familyStore);
          self.grandparentsGen.setRightGrandparentsLeftFamilyMemberId(
            rightGrandparentSelection.leftParent?.familyMemberId ?? null
          );
          self.grandparentsGen.setRightGrandparentsRightFamilyMemberId(
            rightGrandparentSelection.rightParent?.familyMemberId ?? null
          );
        }

        // Create placeholders for unknown right grandparent positions
        // Use tree anchor ID to make placeholder IDs unique per tree build
        if (!self.grandparentsGen.rightGrandparentsLeftFamilyMemberId) {
          const placeholderId = `placeholder-${treeAnchorFamilyMemberId}-right-grandparent-left`;
          this.ensurePlaceholderPerson(placeholderId, 'right-grandparent-left');
          self.grandparentsGen.setRightGrandparentsLeftFamilyMemberId(placeholderId);
        }
        if (!self.grandparentsGen.rightGrandparentsRightFamilyMemberId) {
          const placeholderId = `placeholder-${treeAnchorFamilyMemberId}-right-grandparent-right`;
          this.ensurePlaceholderPerson(placeholderId, 'right-grandparent-right');
          self.grandparentsGen.setRightGrandparentsRightFamilyMemberId(placeholderId);
        }

        // STEP 4: Set children generation
        const children = familyStore.getChildren(treeAnchorFamilyMemberId);
        const childrenFamilyMemberIds = children.map((child) => child.familyMemberId);
        self.childrenGen.setChildrenFamilyMemberIds(childrenFamilyMemberIds);

        self.lastBuilt = new Date();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to build tree:', error);
        throw error;
      } finally {
        self.isBuilding = false;
      }
    },

    /**
     * Update root generation when spouse selection changes
     * Handles two cases:
     * 1. TreeAnchor selects a different spouse: Updates root generation, keeps same TreeAnchor
     * 2. LeftRootFamilyMember (non-TreeAnchor) selects a different spouse: Updates root generation AND changes TreeAnchor to the newly selected spouse, regenerates tree
     * @param personFamilyMemberId - The family member ID of the person whose spouse was selected
     */
    updateRootGenerationForSpouseSelection(personFamilyMemberId: string) {
      if (!familyStore.isLoaded) {
        // eslint-disable-next-line no-console
        console.warn('FamilyStore not loaded, cannot update root generation');
        return;
      }

      const isTreeAnchor = personFamilyMemberId === self.treeAnchorFamilyMemberId;
      const isLeftRootMember = personFamilyMemberId === self.rootGen.leftRootFamilyMemberId;
      const isRightRootMember = personFamilyMemberId === self.rootGen.rightRootFamilyMemberId;

      // Only update if the person is in the root generation
      if (!isLeftRootMember && !isRightRootMember) {
        // Spouse selection for someone not in root generation doesn't affect root generation
        return;
      }

      if (isTreeAnchor) {
        // Case 1: TreeAnchor selects a different spouse
        // Just update the root generation IDs, keep same TreeAnchor, no tree regeneration
        const treeAnchor = familyStore.getPerson(self.treeAnchorFamilyMemberId);
        if (!treeAnchor) {
          // eslint-disable-next-line no-console
          console.warn('Tree anchor not found, cannot update root generation');
          return;
        }

        // Recalculate root generation using the same logic as buildTree
        // This will use the updated selectedSpouseFamilyMemberId from the PersonModel
        const displaySpouseFamilyMemberId = determineDisplaySpouse(treeAnchor);
        const displaySpouse = displaySpouseFamilyMemberId ? familyStore.getPerson(displaySpouseFamilyMemberId) : null;
        const rootPositions = determinePositions(treeAnchor, displaySpouse ?? null);
        self.rootGen.setLeftRootFamilyMemberId(rootPositions.leftFamilyMemberId ?? null);
        self.rootGen.setRightRootFamilyMemberId(rootPositions.rightFamilyMemberId ?? null);
      } else {
        // Case 2: LeftRootFamilyMember (non-TreeAnchor) selects a different spouse
        // The newly selected spouse becomes the new TreeAnchor and we regenerate the tree
        const person = familyStore.getPerson(personFamilyMemberId);
        if (!person) {
          // eslint-disable-next-line no-console
          console.warn('Person not found, cannot update root generation');
          return;
        }

        // Get the newly selected spouse
        const newSpouseFamilyMemberId = person.selectedSpouseFamilyMemberId;
        if (!newSpouseFamilyMemberId) {
          // eslint-disable-next-line no-console
          console.warn('No spouse selected, cannot update root generation');
          return;
        }

        const newSpouse = familyStore.getPerson(newSpouseFamilyMemberId);
        if (!newSpouse) {
          // eslint-disable-next-line no-console
          console.warn('Selected spouse not found, cannot update root generation');
          return;
        }

        // The newly selected spouse becomes the new TreeAnchor
        // Regenerate the entire tree based on the new TreeAnchor
        this.buildTree(newSpouseFamilyMemberId);
      }
    },

    /**
     * Update parents and grandparents generations when a parent selects a different spouse
     * When a parent in ParentsGeneration selects a different spouse:
     * 1. Recalculate parents generation to reflect the new spouse selection (may change which parent is displayed)
     * 2. Update the corresponding grandparent generation to show the newly selected spouse's parents
     * @param familyMemberId - The family member ID of the parent whose spouse was selected
     */
    updateGrandparentsGenerationForSpouseSelection(familyMemberId: string) {
      if (!familyStore.isLoaded) {
        // eslint-disable-next-line no-console
        console.warn('FamilyStore not loaded, cannot update grandparents generation');
        return;
      }

      const parentPerson = familyStore.getPerson(familyMemberId);
      if (!parentPerson) {
        // eslint-disable-next-line no-console
        console.warn('Parent not found in FamilyStore', { familyMemberId });
        return;
      }

      const isLeftParent = familyMemberId === self.parentsGen.leftParentFamilyMemberId;
      const isRightParent = familyMemberId === self.parentsGen.rightParentFamilyMemberId;

      // Only update if the person is in the parents generation
      if (!isLeftParent && !isRightParent) {
        // eslint-disable-next-line no-console
        console.warn('Person is not in parents generation, skipping update', { familyMemberId });
        return;
      }

      // STEP 1: Recalculate parents generation to reflect the new spouse selection
      // This will use the updated selectedSpouseFamilyMemberId from the parent's PersonModel
      const parentSelection = selectDisplayParents(self.treeAnchorFamilyMemberId, familyStore);
      self.parentsGen.setLeftParentFamilyMemberId(parentSelection.leftParent?.familyMemberId ?? null);
      self.parentsGen.setRightParentFamilyMemberId(parentSelection.rightParent?.familyMemberId ?? null);

      // STEP 2: Update grandparents generation based on the newly selected spouse
      // Get the parent person (may have changed if spouse selection affected parent selection)
      const currentLeftParentId = self.parentsGen.leftParentFamilyMemberId;
      const currentRightParentId = self.parentsGen.rightParentFamilyMemberId;

      // Determine which parent's spouse was selected (may have changed after recalculation)
      let parentWithSelectedSpouse: string | null = null;
      if (currentLeftParentId === familyMemberId) {
        parentWithSelectedSpouse = currentLeftParentId;
      } else if (currentRightParentId === familyMemberId) {
        parentWithSelectedSpouse = currentRightParentId;
      } else {
        // The parent may have changed after recalculation, find which one has the selected spouse
        const leftParent = currentLeftParentId ? familyStore.getPerson(currentLeftParentId) : null;
        const rightParent = currentRightParentId ? familyStore.getPerson(currentRightParentId) : null;

        if (leftParent && leftParent.familyMemberId === familyMemberId) {
          parentWithSelectedSpouse = currentLeftParentId;
        } else if (rightParent && rightParent.familyMemberId === familyMemberId) {
          parentWithSelectedSpouse = currentRightParentId;
        }
      }

      if (!parentWithSelectedSpouse) {
        // eslint-disable-next-line no-console
        console.warn('Parent with selected spouse not found after recalculation');
        return;
      }

      const parentWithSpouse = familyStore.getPerson(parentWithSelectedSpouse);
      if (!parentWithSpouse) {
        // eslint-disable-next-line no-console
        console.warn('Parent not found, cannot update grandparents generation');
        return;
      }

      // Get the newly selected spouse
      const newSpouseFamilyMemberId = parentWithSpouse.selectedSpouseFamilyMemberId;
      if (!newSpouseFamilyMemberId) {
        // eslint-disable-next-line no-console
        console.warn('No spouse selected, cannot update grandparents generation');
        return;
      }

      // Use selectDisplayParents to get the newly selected spouse's parents (which become the new grandparents)
      const grandparentSelection = selectDisplayParents(newSpouseFamilyMemberId, familyStore);

      // Determine which side to update based on current parent positions
      const isCurrentlyLeftParent = parentWithSelectedSpouse === currentLeftParentId;
      const isCurrentlyRightParent = parentWithSelectedSpouse === currentRightParentId;

      if (isCurrentlyLeftParent) {
        // Update right grandparents (left parent's spouse is on the right)
        self.grandparentsGen.setRightGrandparentsLeftFamilyMemberId(
          grandparentSelection.leftParent?.familyMemberId ?? null
        );
        self.grandparentsGen.setRightGrandparentsRightFamilyMemberId(
          grandparentSelection.rightParent?.familyMemberId ?? null
        );
      } else if (isCurrentlyRightParent) {
        // Update left grandparents (right parent's spouse is on the left)
        self.grandparentsGen.setLeftGrandparentsLeftFamilyMemberId(
          grandparentSelection.leftParent?.familyMemberId ?? null
        );
        self.grandparentsGen.setLeftGrandparentsRightFamilyMemberId(
          grandparentSelection.rightParent?.familyMemberId ?? null
        );
      }
    },

    /**
     * Update grandparents generation when a grandparent selects a different spouse
     * When a grandparent in GrandparentsGeneration selects a different spouse, update the corresponding
     * grandparent position to show the newly selected spouse
     * @param familyMemberId - The family member ID of the grandparent whose spouse was selected
     */
    updateGrandparentsGenerationForGrandparentSpouseSelection(familyMemberId: string) {
      if (!familyStore.isLoaded) {
        // eslint-disable-next-line no-console
        console.warn('FamilyStore not loaded, cannot update grandparents generation');
        return;
      }

      const {
        leftGrandparentsLeftFamilyMemberId,
        leftGrandparentsRightFamilyMemberId,
        rightGrandparentsLeftFamilyMemberId,
        rightGrandparentsRightFamilyMemberId
      } = self.grandparentsGen;

      // Check which grandparent position this person is in
      const isLeftGrandparentsLeft = familyMemberId === leftGrandparentsLeftFamilyMemberId;
      const isLeftGrandparentsRight = familyMemberId === leftGrandparentsRightFamilyMemberId;
      const isRightGrandparentsLeft = familyMemberId === rightGrandparentsLeftFamilyMemberId;
      const isRightGrandparentsRight = familyMemberId === rightGrandparentsRightFamilyMemberId;

      // Only update if the person is in the grandparents generation
      if (
        !isLeftGrandparentsLeft &&
        !isLeftGrandparentsRight &&
        !isRightGrandparentsLeft &&
        !isRightGrandparentsRight
      ) {
        // Spouse selection for someone not in grandparents generation doesn't affect grandparents generation
        return;
      }

      // Get the grandparent person
      const grandparent = familyStore.getPerson(familyMemberId);
      if (!grandparent) {
        // eslint-disable-next-line no-console
        console.warn('Grandparent not found, cannot update grandparents generation');
        return;
      }

      // Get the newly selected spouse
      const newSpouseFamilyMemberId = grandparent.selectedSpouseFamilyMemberId;
      if (!newSpouseFamilyMemberId) {
        // eslint-disable-next-line no-console
        console.warn('No spouse selected, cannot update grandparents generation');
        return;
      }

      // Update the corresponding grandparent position to show the newly selected spouse
      if (isLeftGrandparentsLeft) {
        // Update left grandparents left position
        self.grandparentsGen.setLeftGrandparentsRightFamilyMemberId(newSpouseFamilyMemberId);
      } else if (isLeftGrandparentsRight) {
        // Update left grandparents right position
        self.grandparentsGen.setLeftGrandparentsLeftFamilyMemberId(newSpouseFamilyMemberId);
      } else if (isRightGrandparentsLeft) {
        // Update right grandparents left position
        self.grandparentsGen.setRightGrandparentsRightFamilyMemberId(newSpouseFamilyMemberId);
      } else if (isRightGrandparentsRight) {
        // Update right grandparents right position
        self.grandparentsGen.setRightGrandparentsLeftFamilyMemberId(newSpouseFamilyMemberId);
      }
    },

    /**
     * Ensure a placeholder person exists in FamilyStore
     * Creates the placeholder if it doesn't exist, otherwise does nothing
     * @param placeholderId - The familyMemberId for the placeholder (e.g., "placeholder-left-parent")
     * @param positionMetadata - The position metadata (e.g., "left-parent")
     */
    ensurePlaceholderPerson(placeholderId: string, positionMetadata: string) {
      if (!familyStore.isLoaded) {
        return;
      }

      // Check if placeholder already exists
      if (familyStore.getPerson(placeholderId)) {
        return;
      }

      // Create placeholder person using FamilyStore's addPerson action
      // This ensures the modification happens within a proper MST action context
      familyStore.addPerson({
        familyMemberId: placeholderId,
        personId: placeholderId, // Use same ID for personId (will be replaced when person is created)
        firstName: 'Unknown',
        lastName: '',
        middleName: null,
        birthDate: null,
        deathDate: null,
        gender: DEFAULT_GENDER,
        spouseFamilyMemberIds: [],
        lastSelectedSpouseFamilyMemberId: null,
        isPlaceholder: true,
        positionMetadata: positionMetadata
      });
    }
  }));

export type HybridPedigreeStoreType = Instance<typeof HybridPedigreeStore>;

const grandparentsGen = GrandparentsGenerationModel.create();
const parentsGen = ParentsGenerationModel.create();
const rootGen = RootGenerationModel.create();
const childrenGen = ChildrenGenerationModel.create();

// Export singleton instance for use in the app
export const hybridPedigreeStore = HybridPedigreeStore.create({
  treeAnchorFamilyMemberId: '',
  grandparentsGen,
  parentsGen,
  rootGen,
  childrenGen
});
